import clsx from 'clsx'
import type { ReactNode } from 'react'
import { Icon } from '@/common/components/icon'
import { Text } from '@/common/components/text'
import * as styles from './PropertyStatsGrid.css'

/** The facts this grid reads; a null fact is left out. */
export interface PropertyStatsFields {
  beds?: number | null
  baths?: number | null
  year_built?: number | null
  sqft?: number | null
}

/** Deal Hub's values for the same facts, shown under any that differ. */
export interface PropertyStatsDealHubValues {
  dh_beds: number | null
  dh_baths: number | null
  dh_year_built: number | null
}

interface PropertyStatItemProps {
  /** Material Symbols icon name. */
  icon: string
  label: string
  value: ReactNode
  /** Shown under the value in the caution color. */
  warning?: ReactNode
}

interface PropertyStatsGridProps {
  property: PropertyStatsFields
  dealHub?: PropertyStatsDealHubValues | null
  className?: string
}

/** One fact with its icon, as the stats grid shows it. */
export function PropertyStatItem({ icon, label, value, warning }: PropertyStatItemProps) {
  return (
    <div className={styles.statItem}>
      <Icon name={icon} size={48} className={styles.statIcon} />
      <div className={styles.statContent}>
        <Text variant="bodyXs" color="muted" className={styles.statLabel}>
          {label}
        </Text>
        <Text variant="body" weight="medium">
          {value}
        </Text>
        {warning && (
          <Text variant="bodyXs" className={styles.dhWarning}>
            {warning}
          </Text>
        )}
      </div>
    </div>
  )
}

/** Beds, baths, year built and house size as an icon grid. */
export function PropertyStatsGrid({ property, dealHub, className }: PropertyStatsGridProps) {
  const { beds, baths, year_built, sqft } = property

  return (
    <div className={clsx(styles.statsGrid, className)}>
      {beds != null && (
        <PropertyStatItem
          icon="bed"
          label="BEDROOMS"
          value={beds}
          warning={dealHub?.dh_beds != null && beds !== dealHub.dh_beds && `Deal Hub: ${dealHub.dh_beds} beds`}
        />
      )}
      {baths != null && (
        <PropertyStatItem
          icon="bathtub"
          label="BATHROOMS"
          value={baths}
          warning={
            dealHub?.dh_baths != null &&
            Number(baths) !== Number(dealHub.dh_baths) &&
            `Deal Hub: ${dealHub.dh_baths} baths`
          }
        />
      )}
      {year_built != null && (
        <PropertyStatItem
          icon="calendar_today"
          label="YEAR BUILT"
          value={year_built}
          warning={
            dealHub?.dh_year_built != null &&
            year_built !== dealHub.dh_year_built &&
            `Deal Hub: ${dealHub.dh_year_built}`
          }
        />
      )}
      {sqft != null && (
        <PropertyStatItem icon="square_foot" label="HOUSE SIZE" value={`${sqft.toLocaleString()} sqft`} />
      )}
    </div>
  )
}
